import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Link,
} from "@react-pdf/renderer";
import { BASE_URL } from "utils/constant";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
  },
  // Organization Header Styles
  orgHeader: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#3B0069",
    paddingBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  orgInfo: {
    flexDirection: "column",
    maxWidth: "60%",
    alignItems: "flex-end", // Align text to the right
  },
  logoContainer: {
    width: 80,
    height: 60,
    justifyContent: "center",
    alignItems: "center",
  },
  logo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  orgName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#3B0069",
    marginBottom: 4,
    textAlign: "right",
  },
  orgText: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
    textAlign: "right",
  },
  // Document Header (Title etc)
  docHeader: {
    marginBottom: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  docTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#3B0069",
    // textTransform: "uppercase",
  },
  docMeta: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  metaText: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 2,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: "#F3F4F6",
    padding: 5,
    color: "#3B0069",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
  },
  label: {
    fontSize: 10,
    color: "#666666",
  },
  value: {
    fontSize: 10,
    textAlign: "right",
    color: "#333333",
    fontWeight: "medium",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#3B0069",
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#3B0069",
  },
  totalValue: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "right",
    color: "#3B0069",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 10,
    fontSize: 8,
    color: "#999999",
    textAlign: "center",
  },
  materialsTable: {
    marginTop: 5,
  },
  itemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 0.5,
    borderBottomColor: "#EEEEEE",
    paddingVertical: 6,
    alignItems: "center",
  },
  tableHeader: {
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  // Specific column widths for MR
  colMaterial: { width: "35%", fontSize: 9 },
  colCategory: { width: "20%", fontSize: 9, textAlign: "center" },
  colQty: { width: "15%", fontSize: 9, textAlign: "center" },
  colUom: { width: "10%", fontSize: 9, textAlign: "center" },
  // For PO/Quotation
  colName: { width: "35%", fontSize: 9 },
  colNormal: { width: "15%", fontSize: 9, textAlign: "center" },
  colRight: { width: "20%", fontSize: 9, textAlign: "right" },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  summaryLabel: {
    fontSize: 10,
    color: "#666666",
  },
  summaryValue: {
    fontSize: 10,
    textAlign: "right",
    color: "#333333",
  },
});

const getOrgDetails = () => {
  try {
    const org = localStorage.getItem("organization");
    if (org) return JSON.parse(org);
  } catch (e) {
    console.error("Error parsing organization details", e);
  }
  return null;
};

const OrgHeader = ({ title }) => {
  const org = getOrgDetails();
  if (!org) return null;

  return (
    <View style={styles.orgHeader}>
      {/* Left: Logo + Title stacked */}
      <View style={{ flexDirection: "column", gap: 6 }}>
        <View style={styles.logoContainer}>
          {org.logo_image_file ? (
            <Image src={org.logo_image_file} style={styles.logo} />
          ) : (
            <Text style={{ fontSize: 20, fontWeight: "bold", color: "#EEE" }}>
              LOGO
            </Text>
          )}
        </View>
        {title && (
          <Text style={[styles.docTitle, { marginTop: 4 }]}>{title}</Text>
        )}
      </View>

      {/* Right: Org Info */}
      <View style={styles.orgInfo}>
        <Text style={styles.orgName}>{org.organization_name}</Text>
        <Text style={styles.orgText}>{org.business_address}</Text>
        <Text style={styles.orgText}>
          {org.city ? `${org.city} ` : ""}
          {org.state} - {org.pincode}
        </Text>
        {org.contact_number && (
          <Text style={styles.orgText}>Ph: {org.contact_number}</Text>
        )}
        {org.user_email && <Text style={styles.orgText}>{org.user_email}</Text>}
        {org.website && <Text style={styles.orgText}>{org.website}</Text>}
      </View>
    </View>
  );
};
export const MaterialRequestReceiptPDF = ({
  request,
  warehouseMaterials = [],
}) => {
  const formatUom = (rawUom) => {
    if (!rawUom) return "";
    const u = String(rawUom).trim().toLowerCase();
    if (u.startsWith("kilogram")) return "kgs";
    if (u === "kg" || u === "kgs") return "kgs";
    return rawUom;
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const safeLines = Array.isArray(request?.material_request_lines)
    ? request.material_request_lines
    : [];

  const getWarehouseQty = (line) => {
    const match = warehouseMaterials.find(
      (m) => m?.material?.id === line.material,
    );
    return match?.current_quantity ?? 0;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader title="Material Request" />

        {/* Request Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Request Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>MR Number</Text>
            <Text style={styles.value}>
              {request?.mr_number || request?.code || "N/A"}
            </Text>
          </View>
          {request?.created_on && (
            <View style={styles.row}>
              <Text style={styles.label}>Raised on</Text>
              <Text style={styles.value}>{formatDate(request.created_on)}</Text>
            </View>
          )}
          {request?.expectedBy && (
            <View style={styles.row}>
              <Text style={styles.label}>Delivery expected by</Text>
              <Text style={styles.value}>{formatDate(request.expectedBy)}</Text>
            </View>
          )}

          {request?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{request.site_name}</Text>
            </View>
          )}

          {(request?.site_address || request?.city || request?.pincode) && (
            <View style={styles.row}>
              <Text style={styles.label}>Site Address</Text>
              <Text style={styles.value}>
                {request.site_address ? `${request.site_address}, ` : ""}
                {request.city ? `${request.city} ` : ""}
                {request.pincode ? `- ${request.pincode}` : ""}
              </Text>
            </View>
          )}
          {/* {request?.raisedBy && (
            <View style={styles.row}>
              <Text style={styles.label}>Raised by</Text>
              <Text style={styles.value}>{request.raisedBy}</Text>
            </View>
          )} */}
          {typeof request?.items === "number" && (
            <View style={styles.row}>
              <Text style={styles.label}>Total items requested</Text>
              <Text style={styles.value}>{request.items}</Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {safeLines.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Requested Materials</Text>
            <View style={styles.materialsTable}>
              {/* Header */}
              <View style={[styles.itemRow, styles.tableHeader]}>
                <Text style={[styles.colMaterial, { fontWeight: "bold" }]}>
                  Material
                </Text>
                <Text style={[styles.colCategory, { fontWeight: "bold" }]}>
                  Category
                </Text>
                <Text style={[styles.colQty, { fontWeight: "bold" }]}>
                  Quantity
                </Text>
                <Text style={[styles.colUom, { fontWeight: "bold" }]}>UOM</Text>
              </View>

              {safeLines.map((line, idx) => (
                <View style={styles.itemRow} key={line.id || idx}>
                  <Text style={styles.colMaterial}>
                    {line.material_name || "N/A"}
                  </Text>
                  <Text style={styles.colCategory}>
                    {line.subcategory || "N/A"}
                  </Text>
                  <Text style={styles.colQty}>{line.quantity}</Text>
                  <Text style={styles.colUom}>
                    {formatUom(line.uom) || "N/A"}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer note */}
        <View style={styles.footer}>
          <Text>
            This receipt captures the material request details for
            record-keeping and sharing.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const PurchaseOrderReceiptPDF = ({ po, poCode, vendor, partPayments = [] }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const purchaseAmount =
    po?.lines?.reduce(
      (total, item) => total + item.unit_price * item.quantity,
      0,
    ) || 0;

  const gstAmount =
    po?.lines?.reduce((total, item) => total + item.tax_amount, 0) || 0;

  const grandTotal = purchaseAmount + gstAmount;
  const safePartPayments = Array.isArray(partPayments) ? partPayments : [];

  const getFileNameFromUrl = (url) => {
    if (!url) return "";
    try {
      const cleanUrl = url.split("?")[0];
      const segments = cleanUrl.split("/");
      const lastSegment = segments[segments.length - 1];
      return lastSegment || "Attachment";
    } catch (e) {
      return "Attachment";
    }
  };

  const resolveFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader title="Purchase Order" />

        {/* Header */}
        <View style={styles.docHeader}>
          <View>
            {/* <Text style={styles.docTitle}>Purchase Order</Text> */}
            <Text style={styles.metaText}>PO No: {poCode || "N/A"}</Text>
            <Text style={styles.metaText}>
              Status: {po?.status?.toUpperCase() || "N/A"}
            </Text>
          </View>
          <View style={styles.docMeta}>
            <Text style={styles.metaText}>
              Date: {formatDate(po?.order_date)}
            </Text>
          </View>
        </View>

        {/* Vendor Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Vendor Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Vendor Name</Text>
            <Text style={styles.value}>{vendor || "N/A"}</Text>
          </View>
          {po?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{po.site_name}</Text>
            </View>
          )}

          {(po?.site_address || po?.city || po?.pincode) && (
            <View style={styles.row}>
              <Text style={styles.label}>Site Address</Text>
              <Text style={styles.value}>
                {po.site_address ? `${po.site_address}, ` : ""}
                {po.city ? `${po.city} ` : ""}
                {po.pincode ? `- ${po.pincode}` : ""}
              </Text>
            </View>
          )}
          {!po?.site && (
            <View style={styles.row}>
              <Text style={styles.label}>Delivery Location</Text>
              <Text style={styles.value}>Warehouse</Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {po?.lines?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Materials</Text>
            <View style={[styles.itemRow, styles.tableHeader]}>
              <Text style={[styles.colName, { fontWeight: "bold" }]}>
                Material Name
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                Qty
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                UOM
              </Text>
              <Text style={[styles.colRight, { fontWeight: "bold" }]}>
                Unit Price
              </Text>
              <Text style={[styles.colNormal, { fontWeight: "bold" }]}>
                GST
              </Text>
              <Text style={[styles.colRight, { fontWeight: "bold" }]}>
                Total
              </Text>
            </View>
            {po.lines.map((item, idx) => {
              const gstPercentage =
                item.unit_price * item.quantity > 0
                  ? (
                      (item.tax_amount / (item.unit_price * item.quantity)) *
                      100
                    ).toFixed(2)
                  : 0;

              return (
                <View style={styles.itemRow} key={idx}>
                  <Text style={styles.colName}>
                    {item.material_name || "N/A"}
                  </Text>
                  <Text style={styles.colNormal}>{item.quantity}</Text>
                  <Text style={styles.colNormal}>{item.uom || "N/A"}</Text>
                  <Text style={styles.colRight}>
                    {item.unit_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                  <Text style={styles.colNormal}>{gstPercentage}%</Text>
                  <Text style={styles.colRight}>
                    {item.total_price?.toLocaleString("en-IN") || "0"}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Payment Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Summary</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Purchase Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {purchaseAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>GST Amount</Text>
            <Text style={styles.summaryValue}>
              Rs. {gstAmount.toLocaleString("en-IN")}
            </Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Grand Total</Text>
            <Text style={styles.totalValue}>
              Rs. {grandTotal.toLocaleString("en-IN")}
            </Text>
          </View>
        </View>

        {/* Payment Method */}
        {po?.payment_method && (
          <View style={styles.section}>
            <View style={styles.row}>
              <Text style={styles.label}>Payment Method</Text>
              <Text style={styles.value}>{po.payment_method}</Text>
            </View>
          </View>
        )}

        {/* {safePartPayments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment History</Text>
            {safePartPayments.map((payment, idx) => (
              <View key={payment.id || idx}>
                <View style={styles.row}>
                  <View>
                    <Text style={styles.label}>
                      Rs. {payment.amount?.toLocaleString("en-IN") || "0"}
                    </Text>
                    {payment.payment_method && (
                      <Text style={styles.label}>{payment.payment_method}</Text>
                    )}
                    {payment.remarks && (
                      <Text style={styles.label}>{payment.remarks}</Text>
                    )}
                  </View>
                  <View style={{ alignItems: "flex-end" }}>
                    {payment.created_on && (
                      <Text style={styles.value}>
                        {formatDate(payment.created_on)}
                      </Text>
                    )}
                  </View>
                </View>

                {payment.file && (
                  <View
                    style={{
                      marginTop: 6,
                      marginBottom: 8,
                      borderWidth: 1,
                      borderColor: "#e5e7eb",
                      borderRadius: 4,
                      padding: 4,
                    }}
                  >
                    <Text style={[styles.label, { marginBottom: 4 }]}>
                      Receipt Attachment:
                    </Text>
                    <Image
                      src={resolveFileUrl(payment.file)}
                      style={{
                        width: "100%",
                        height: 200,
                      }}
                    />
                  </View>
                )}
              </View>
            ))}
          </View>
        )} */}
        {safePartPayments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment History</Text>
            {safePartPayments.map((payment, idx) => {
              const fileUrl = payment.file
                ? resolveFileUrl(payment.file)
                : null;
              const fileName = payment.file
                ? getFileNameFromUrl(payment.file)
                : null;
              const ext = fileName
                ? fileName.split(".").pop().toLowerCase()
                : "";
              const isImage = ["jpg", "jpeg", "png", "webp", "gif"].includes(
                ext,
              );
              const isPdf = ext === "pdf";

              return (
                <View key={payment.id || idx}>
                  <View style={styles.row}>
                    <View>
                      <Text style={styles.label}>
                        Rs. {payment.amount?.toLocaleString("en-IN") || "0"}
                      </Text>
                      {payment.payment_method && (
                        <Text style={styles.label}>
                          {payment.payment_method}
                        </Text>
                      )}
                      {payment.remarks && (
                        <Text style={styles.label}>{payment.remarks}</Text>
                      )}
                    </View>
                    <View style={{ alignItems: "flex-end" }}>
                      {payment.created_on && (
                        <Text style={styles.value}>
                          {formatDate(payment.created_on)}
                        </Text>
                      )}
                    </View>
                  </View>

                  {/* Image attachment */}
                  {fileUrl && isImage && (
                    <View
                      style={{
                        marginTop: 6,
                        marginBottom: 8,
                        borderWidth: 1,
                        borderColor: "#e5e7eb",
                        borderRadius: 4,
                        padding: 4,
                      }}
                    >
                      <Text style={[styles.label, { marginBottom: 4 }]}>
                        Receipt Attachment:
                      </Text>
                      <Image
                        src={fileUrl}
                        style={{
                          width: 300,
                          height: 200,
                          alignSelf: "center",
                        }}
                      />
                    </View>
                  )}

                  {/* PDF attachment — show as clickable link */}
                  {fileUrl && isPdf && (
                    <View
                      style={{
                        marginTop: 6,
                        marginBottom: 8,
                        borderWidth: 1,
                        borderColor: "#e5e7eb",
                        borderRadius: 4,
                        padding: 8,
                        backgroundColor: "#f9fafb",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Text style={[styles.label, { marginBottom: 0 }]}>
                        📄 Receipt Attachment (PDF):{" "}
                      </Text>
                      <Link
                        src={fileUrl}
                        style={{ color: "#3B0069", fontSize: 9 }}
                      >
                        {fileName}
                      </Link>
                    </View>
                  )}

                  {/* Unknown file type — fallback link */}
                  {fileUrl && !isImage && !isPdf && (
                    <View
                      style={{
                        marginTop: 6,
                        marginBottom: 8,
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Text style={[styles.label, { marginBottom: 0 }]}>
                        Attachment:{" "}
                      </Text>
                      <Link
                        src={fileUrl}
                        style={{ color: "#3B0069", fontSize: 9 }}
                      >
                        {fileName}
                      </Link>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
        <View style={styles.footer}>
          <Text>
            This is a computer-generated receipt and does not require signature.
          </Text>
          <Text>For any queries, please contact support.</Text>
        </View>
      </Page>
    </Document>
  );
};

/**
 * Quotation receipt: simpler PDF listing material names, quantities and tags.
 * Designed to be downloaded from the Quotation Summary UI.
 */
export const QuotationReceiptPDF = ({ rfq }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const formatUom = (rawUom) => {
    if (!rawUom) return "";
    const u = String(rawUom).trim().toLowerCase();
    if (u.startsWith("kilogram")) return "kgs";
    if (u === "kg" || u === "kgs") return "kgs";
    return rawUom;
  };

  const safeLines = Array.isArray(rfq?.lines) ? rfq.lines : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <OrgHeader title="Quotation Receipt" />

        {/* Request Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Quotation Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>RFQ Number</Text>
            <Text style={styles.value}>
              {rfq?.rfq_number || rfq?.id || "N/A"}
            </Text>
          </View>
          {(rfq?.mr_number || rfq?.material_request_number || rfq?.mr_code) && (
            <View style={styles.row}>
              <Text style={styles.label}>MR Number</Text>
              <Text style={styles.value}>
                {rfq?.mr_number || rfq?.material_request_number || rfq?.mr_code}
              </Text>
            </View>
          )}
          {rfq?.created_on && (
            <View style={styles.row}>
              <Text style={styles.label}>Raised on</Text>
              <Text style={styles.value}>{formatDate(rfq.created_on)}</Text>
            </View>
          )}

          {rfq?.site_name && (
            <View style={styles.row}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{rfq.site_name}</Text>
            </View>
          )}

          {(rfq?.site_address || rfq?.city || rfq?.pincode) && (
            <View style={styles.row}>
              <Text style={styles.label}>Site Address</Text>
              <Text style={styles.value}>
                {rfq.site_address ? `${rfq.site_address}, ` : ""}
                {rfq.city ? `${rfq.city} ` : ""}
                {rfq.pincode ? `- ${rfq.pincode}` : ""}
              </Text>
            </View>
          )}
        </View>

        {/* Material List */}
        {safeLines.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Requested Materials</Text>
            <View style={styles.materialsTable}>
              {/* Header */}
              <View style={[styles.itemRow, styles.tableHeader]}>
                <Text style={[styles.colMaterial, { fontWeight: "bold" }]}>
                  Material
                </Text>
                <Text style={[styles.colCategory, { fontWeight: "bold" }]}>
                  Category
                </Text>
                <Text style={[styles.colQty, { fontWeight: "bold" }]}>
                  Quantity
                </Text>
                <Text style={[styles.colUom, { fontWeight: "bold" }]}>UOM</Text>
              </View>

              {safeLines.map((line, idx) => (
                <View style={styles.itemRow} key={line.id || idx}>
                  <Text style={styles.colMaterial}>
                    {line.material_name || "N/A"}
                  </Text>
                  <Text style={styles.colCategory}>
                    {line.subcategory || "N/A"}
                  </Text>
                  <Text style={styles.colQty}>{line.quantity}</Text>
                  <Text style={styles.colUom}>
                    {formatUom(line.uom) || "N/A"}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer note */}
        <View style={styles.footer}>
          <Text>
            This receipt lists the materials in the RFQ together with requested
            quantities.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PurchaseOrderReceiptPDF;
